/**
 * clearCellsInRange.ts - Clear cells within a selection box.
 *
 * Exposes the public {@link clearCellsInRange} / {@link clearSelection} methods (orchestration:
 * analytics + persistence) and the pure {@link _clearCellsTransform} they wrap.
 */

import produce from 'immer';

import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import type {Cell} from 'notebook/tables/constants/CellConstants';
import {type Table} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {type AffectedCell, type TableMutationResult} from 'notebook/tables/excess/ops/TableOpConstants';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {
  computeWorksheetInfo,
  persistTable,
  type TableMutationParams,
} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection} from 'notebook/tables/store/logic/mutations/selection';
import TableUtil from 'notebook/tables/TableUtil';
import {getBaseCell} from 'notebook/tables/typed-values/DataTypes';
import {trackCrossStepReferenceBulkDelete} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';

/**
 * Clear all cells within a selection box, then track analytics and persist the change.
 *
 * @param params - The shared mutation dependencies.
 * @param selectionBox - The box defining which cells to clear.
 */
export function clearCellsInRange(params: TableMutationParams, selectionBox: MultiSelectionBox): void {
  const {table, fileId, tableNamespace} = params.getTableProps();

  // Execute pure mutation
  const result = _clearCellsTransform(table, selectionBox);

  // Track cross-step reference analytics
  const oldCells = result.affectedCells.map((c) => c.oldCell).filter((c): c is Cell => c != null);
  trackCrossStepReferenceBulkDelete(oldCells, computeWorksheetInfo(tableNamespace, fileId));

  // Persist the change
  persistTable(params, result);

  // Track container validity analytics
  if (oldCells.length > 0) {
    trackContainerValidityForTableEvents(fileId, table.apiId!, [
      {
        event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE,
        cells: oldCells,
      },
    ]);
  }
}

/**
 * Clear all cells within the current selection, expanding to fully contain any intersecting merged
 * regions.
 *
 * Checkpoints the current selection for undo, clears the cells, and leaves cell editing. No-op when
 * there is no real selection.
 */
export function clearSelection(params: TableMutationParams): void {
  const model = params.getTableModel();
  const {hasRealSelection, expandedBox} = model.selection;

  // Nothing to clear when the selection does not cover any real cells.
  if (!hasRealSelection) {
    return;
  }

  saveSelection(params);

  // `hasRealSelection` implies a selection box, but the box is still read defensively
  // so a missing one skips the cell writes rather than throwing.
  if (expandedBox != null) {
    clearCellsInRange(params, expandedBox);
  }

  const {table, tableNamespace, fileId, documentName} = params.getTableProps();
  SpreadsheetEvalUtil.setMultiEditingForTable(
    {tableName: table.name, documentName: documentName ?? null},
    tableNamespace,
    fileId,
    {editing: false, index: null, text: '', typedValue: null, selection: null}
  );
}

/**
 * Clear all cells within a selection box.
 *
 * @param table - The current table
 * @param selectionBox - The box defining which cells to clear
 * @returns The mutation result with new table, operation, and affected cells
 */
export function _clearCellsTransform(table: Table, selectionBox: MultiSelectionBox): TableMutationResult {
  const affectedCells: Array<AffectedCell> = [];
  const tableOpCells: Array<{row: number; column: number}> = [];

  const newTable = produce(table, (draftTable) => {
    for (let i = selectionBox.minI; i <= selectionBox.maxI; i++) {
      for (let j = selectionBox.minJ; j <= selectionBox.maxJ; j++) {
        const oldCell = table.table[i]?.[j];
        if (oldCell != null) {
          const newCell = TableUtil.makeCell('', getBaseCell(oldCell));
          draftTable.table[i][j] = newCell;
          affectedCells.push({row: i, column: j, oldCell, newCell});
          tableOpCells.push({row: i, column: j});
        }
      }
    }
  });

  return {
    table: newTable,
    tableOp: {type: 'update-cells', cells: tableOpCells},
    affectedCells,
  };
}
